import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../../lib/toast';
import { API, auth, errMsg, PrimaryBtn, inputCls } from '../finance/shared';
import { PAYMENT_METHOD_META, PAYMENT_METHOD_ORDER } from './bookingStatusMeta';
import { PaymentMethodTitle } from './PaymentMethodBadge';

export default function PaymentSettingsCard() {
  const [methods, setMethods] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/venues/payment-settings`, auth());
      setMethods(res.data.methods || []);
    } catch { setMethods([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (methodId, changes) => {
    setMethods((prev) => prev.map((m) => (m.method_id === methodId ? { ...m, ...changes } : m)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/venues/payment-settings`, { methods }, auth());
      toast.success('تم حفظ طرق الدفع');
    } catch (e) { toast.error(errMsg(e, 'فشل حفظ طرق الدفع')); } finally { setSaving(false); }
  };

  if (methods === null) return <div className="h-32 animate-pulse rounded-2xl bg-slate-100" data-testid="payment-settings-loading" />;

  const ordered = PAYMENT_METHOD_ORDER.map((id) => methods.find((m) => m.method_id === id)).filter(Boolean);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="payment-settings-card">
      <p className="text-sm font-bold text-slate-700">طرق الدفع المتاحة للعميل</p>
      <p className="mt-1 text-xs text-slate-500">فعّل الطرق التي تقبلها في حجوزات ملاعبك — يرى العميل فقط الطرق المفعّلة</p>
      <div className="mt-4 space-y-3">
        {ordered.map((m) => {
          const needsDetails = m.method_id !== 'pay_on_site';
          return (
            <div key={m.method_id} className={`rounded-xl border p-3.5 transition-colors ${m.enabled ? 'border-orange-200 bg-orange-50/40' : 'border-slate-200'}`} data-testid={`payment-method-row-${m.method_id}`}>
              <label className="flex items-center justify-between gap-2">
                <PaymentMethodTitle methodId={m.method_id} />
                <input type="checkbox" checked={!!m.enabled} onChange={(e) => patch(m.method_id, { enabled: e.target.checked })} data-testid={`payment-method-toggle-${m.method_id}`} className="h-5 w-9 accent-orange-600" />
              </label>
              {needsDetails && m.enabled && (
                <div className="mt-3 space-y-2">
                  <textarea
                    className={`${inputCls} h-20 resize-none`}
                    placeholder={m.method_id === 'bank_transfer' ? 'اسم البنك، رقم الآيبان، اسم صاحب الحساب' : 'رقم المحفظة / بيانات الاستلام'}
                    value={m.details || ''}
                    onChange={(e) => patch(m.method_id, { details: e.target.value })}
                    data-testid={`payment-method-details-${m.method_id}`}
                  />
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                    <input type="checkbox" checked={!!m.proof_required} onChange={(e) => patch(m.method_id, { proof_required: e.target.checked })} data-testid={`payment-method-proof-toggle-${m.method_id}`} />
                    طلب إثبات الدفع (صورة/PDF) من العميل قبل اعتباره مؤكداً
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4">
        <PrimaryBtn onClick={save} disabled={saving} data-testid="payment-settings-save-btn">{saving ? 'جارٍ الحفظ...' : 'حفظ طرق الدفع'}</PrimaryBtn>
      </div>
    </div>
  );
}
